import { useState, type CSSProperties } from 'react';
import { Badge, Button, Input, Modal, Tabs } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { DocBlock, DocRow } from '../../components/document';
import {
  useZamestnanci,
  useSharedZamestnanci,
  useCreateZamestnanec,
  useUpdateZamestnanec,
  useDeleteZamestnanec,
  useCreateZamestnanecPolozka,
  useUpdateZamestnanecPolozka,
  useDeleteZamestnanecPolozka,
  useAcceptZamestnanecPolozka,
  useZamestnanecShares,
  useAddZamestnanecShare,
  useRemoveZamestnanecShare,
} from '../../api/hooks';
import type { ZamestnanecKarta, ZamestnanecPolozka } from '../../api/schemas';
import { apiFetch } from '../../lib/api';
import { obnovaStav, daysUntil } from './obnova';

const viewTabs = [
  { id: 'moje', label: 'Moje karty' },
  { id: 'nasdilene', label: 'Nasdílené mně' },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDatum(iso?: string | null): string {
  const m = (iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}. ${m[2]}. ${m[1]}` : '—';
}

function pocetKObnove(karta: ZamestnanecKarta): number {
  return (karta.polozky ?? []).filter((p) => {
    const s = obnovaStav(p.datum_obnovy);
    return s === 'po' || s === 'blizko';
  }).length;
}

/** Barevné zvýraznění řádku podle blížící se obnovy. */
function obnovaRowStyle(iso?: string | null): CSSProperties | undefined {
  const s = obnovaStav(iso);
  if (s === 'po') return { background: 'var(--sd-danger-soft, #fdecea)' };
  if (s === 'blizko') return { background: 'var(--sd-warning-soft, #fff8e6)' };
  return undefined;
}

function ObnovaBadge({ iso }: { iso?: string | null }) {
  const s = obnovaStav(iso);
  if (!iso) return <span style={{ color: 'var(--sd-text-dim)' }}>—</span>;
  const d = daysUntil(iso);
  if (s === 'po') return <Badge tone="danger" pill dot>{fmtDatum(iso)} · prošlo</Badge>;
  if (s === 'blizko') return <Badge tone="yellow" pill dot>{fmtDatum(iso)} · za {d} d</Badge>;
  return <span style={{ fontFamily: 'var(--sd-font-mono)' }}>{fmtDatum(iso)}</span>;
}

/** Vygeneruje čisté PDF přes backend (Playwright) a stáhne ho. */
async function downloadPdf(html: string, filename: string) {
  try {
    const blob = (await apiFetch('/pdf/generate', { method: 'POST', body: { html, filename } })) as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    window.alert('Nepodařilo se vygenerovat PDF.');
  }
}

/** Sestaví tisknutelné HTML — osobní evidenční karta zaměstnance pro výdej OOPP (k podpisu). */
function buildKartaHtml(karta: ZamestnanecKarta): string {
  const esc = (s: unknown) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
  const den = (iso?: string | null) => (iso ? fmtDatum(iso) : '');
  const rows = (karta.polozky ?? [])
    .map((p) => {
      // „Podpis zaměstnance (převzetí)" — pokud bylo elektronicky potvrzeno, vyplníme datum; jinak prázdné pro ruční podpis.
      const prevzeti = p.prijato ? `převzato ${esc(fmtDatum(p.prijato_at))}` : '';
      return `<tr>
        <td>${esc(p.nazev)}${p.pocet && p.pocet !== 1 ? ` <span class="qty">(${p.pocet}×)</span>` : ''}</td>
        <td style="text-align:center">${esc(den(p.datum_predani))}</td>
        <td style="text-align:center">${prevzeti}</td>
        <td style="text-align:center">${esc(den(p.datum_vraceni))}</td>
        <td>${esc(p.poznamka)}</td>
      </tr>`;
    })
    .join('');
  // Doplníme pár prázdných řádků pro ruční zápis (jako v papírovém vzoru).
  const prazdne = Array.from({ length: Math.max(0, 5 - (karta.polozky ?? []).length) })
    .map(() => '<tr><td>&nbsp;</td><td></td><td></td><td></td><td></td></tr>')
    .join('');
  const velikost = `${esc(karta.velikost_odevu) || '__________'} / ${esc(karta.velikost_obuvi) || '__________'}`;
  return `<!doctype html><html lang="cs"><head><meta charset="utf-8"><title>Evidenční karta zaměstnance — OOPP</title>
<style>
  body{font-family:Arial,Helvetica,sans-serif;margin:0;color:#1a1a1a}
  h1{font-size:16px;color:#12325a;margin:0 0 14px}
  .hdr{font-size:12.5px;line-height:2;margin-bottom:16px}
  .hdr b{color:#12325a}
  .hdr .fill{border-bottom:1px solid #888;display:inline-block;min-width:220px;padding:0 4px}
  table.items{width:100%;border-collapse:collapse;font-size:11.5px}
  table.items th{background:#12325a;color:#fff;padding:7px 8px;text-align:left;vertical-align:top}
  table.items td{border:1px solid #dde3ed;padding:7px 8px;height:18px}
  table.items .qty{color:#777}
  .decl{font-size:11px;font-style:italic;color:#444;margin:14px 0 30px;line-height:1.5}
  .sign{width:100%;border-collapse:collapse;font-size:12px;margin-top:46px}
  .sign td{width:50%;text-align:center;padding-top:6px;border-top:1px solid #333;vertical-align:top;color:#333}
  .sign .name{font-weight:bold}
  .sign .role{color:#666;font-size:11px}
  .sign tr.gap td{border:none;height:40px;padding:0}
  @page{margin:14mm}
</style></head><body>
  <h1>Osobní evidenční karta zaměstnance pro výdej OOPP</h1>
  <div class="hdr">
    <b>Jméno a příjmení:</b> <span class="fill">${esc(karta.jmeno)}</span>
    &nbsp;&nbsp; <b>Profese / pracovní zařazení:</b> <span class="fill">${esc(karta.pozice)}</span><br/>
    <b>Datum nástupu:</b> <span class="fill">${esc(den(karta.datum_nastupu))}</span>
    &nbsp;&nbsp; <b>Velikost oděvu / obuvi:</b> <span class="fill" style="min-width:140px">${velikost}</span>
  </div>
  <table class="items"><thead><tr>
    <th>Vydaný předmět (OOPP)</th>
    <th style="width:84px">Datum vydání</th>
    <th style="width:118px">Podpis zaměstnance (převzetí)</th>
    <th style="width:84px">Datum vrácení / vyřazení</th>
    <th style="width:150px">Důvod vyřazení / Poznámka</th>
  </tr></thead><tbody>${rows}${prazdne}</tbody></table>
  <div class="decl">Svým podpisem potvrzuji, že jsem uvedené OOPP převzal v provozuschopném stavu, byl jsem poučen o způsobu jejich správného používání a jsem si vědom povinnosti je na pracovišti striktně nosit.</div>
  <table class="sign">
    <tr class="gap"><td></td><td></td></tr>
    <tr>
      <td><div class="name">${esc(karta.jednatel) || '&nbsp;'}</div><div class="role">Jednatel společnosti</div></td>
      <td><div class="name">${esc(karta.bozp_technik) || '&nbsp;'}</div><div class="role">Osoba odborně způsobilá v prevenci rizik</div></td>
    </tr>
  </table>
</body></html>`;
}

// ─── STRÁNKA ─────────────────────────────────────────────────────────────────

export function KartyZamestnancuPage() {
  const [view, setView] = useState('moje');
  return (
    <ListPage
      breadcrumb={['BOZP', 'Karty zaměstnanců']}
      title="Karty zaměstnanců"
      subtitle="Evidence předaného majetku a OOPP · potvrzení převzetí"
      tools={<Tabs items={viewTabs} value={view} onChange={setView} />}
    >
      {view === 'moje' ? <MojeKarty /> : <NasdileneKarty />}
    </ListPage>
  );
}

// ─── MOJE KARTY (tvůrce) ─────────────────────────────────────────────────────

const PRAZDNA_KARTA = {
  jmeno: '', pozice: '', firma: '', telefon: '', email: '',
  datum_nastupu: '', velikost_odevu: '', velikost_obuvi: '',
  jednatel: '', bozp_technik: '', poznamka: '',
};

function MojeKarty() {
  const { data: karty = [], isLoading, error } = useZamestnanci();
  const create = useCreateZamestnanec();
  const update = useUpdateZamestnanec();
  const del = useDeleteZamestnanec();

  const [detailId, setDetailId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(PRAZDNA_KARTA);

  const selected = detailId != null ? karty.find((k) => k.id === detailId) ?? null : null;

  function setF(k: keyof typeof PRAZDNA_KARTA, v: string) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function openNew() {
    setEditId(null);
    setForm(PRAZDNA_KARTA);
    setFormOpen(true);
  }

  function openEdit(k: ZamestnanecKarta) {
    setEditId(k.id);
    setForm({
      jmeno: k.jmeno, pozice: k.pozice, firma: k.firma, telefon: k.telefon, email: k.email,
      datum_nastupu: k.datum_nastupu || '', velikost_odevu: k.velikost_odevu, velikost_obuvi: k.velikost_obuvi,
      jednatel: k.jednatel, bozp_technik: k.bozp_technik, poznamka: k.poznamka,
    });
    setDetailId(null);
    setFormOpen(true);
  }

  async function handleSave() {
    const payload = {
      jmeno: form.jmeno.trim(),
      pozice: form.pozice.trim(),
      firma: form.firma.trim(),
      telefon: form.telefon.trim(),
      email: form.email.trim(),
      datum_nastupu: form.datum_nastupu || null,
      velikost_odevu: form.velikost_odevu.trim(),
      velikost_obuvi: form.velikost_obuvi.trim(),
      jednatel: form.jednatel.trim(),
      bozp_technik: form.bozp_technik.trim(),
      poznamka: form.poznamka.trim(),
    };
    if (editId) await update.mutateAsync({ id: editId, payload });
    else await create.mutateAsync(payload);
    setFormOpen(false);
  }

  async function handleDeleteCard(id: number) {
    if (!window.confirm('Opravdu smazat celou kartu zaměstnance i s evidencí?')) return;
    await del.mutateAsync(id);
    setDetailId(null);
  }

  const columns: ListColumn<ZamestnanecKarta>[] = [
    { key: 'jmeno', label: 'Zaměstnanec', primary: true, render: (k) => k.jmeno || '—' },
    { key: 'pozice', label: 'Pozice', width: '180px', dim: true, render: (k) => k.pozice || '—' },
    { key: 'firma', label: 'Firma', width: '180px', dim: true, render: (k) => k.firma || '—' },
    { key: 'pocet', label: 'Položek', width: '90px', align: 'right', mono: true, render: (k) => String((k.polozky ?? []).length) },
    {
      key: 'obnova',
      label: 'K obnově',
      width: '120px',
      render: (k) => {
        const n = pocetKObnove(k);
        return n > 0 ? <Badge tone="yellow" pill dot>{n}</Badge> : <span style={{ color: 'var(--sd-text-dim)' }}>—</span>;
      },
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--sd-text-dim)' }}>
          {isLoading ? 'Načítám…' : error ? 'Chyba načítání' : `${karty.length} karet`}
        </div>
        <Button variant="primary" onClick={openNew}>+ Nová karta</Button>
      </div>

      <ListTable
        columns={columns}
        rows={karty}
        rowKey={(k) => k.id}
        onRowClick={(k) => setDetailId(k.id)}
        emptyTitle="Žádné karty zaměstnanců"
        emptyText="Založte kartu zaměstnance a evidujte předaný majetek a ochranné pomůcky."
      />

      {/* Detail karty */}
      <Modal
        open={!!selected}
        onClose={() => setDetailId(null)}
        title={selected ? `Karta — ${selected.jmeno || 'zaměstnanec'}` : ''}
        size="xl"
        footer={
          selected && (
            <>
              <Button variant="ghost" onClick={() => setDetailId(null)}>Zavřít</Button>
              <Button variant="danger" onClick={() => handleDeleteCard(selected.id)} disabled={del.isPending}>🗑️ Smazat kartu</Button>
              <Button variant="secondary" onClick={() => openEdit(selected)}>✎ Upravit údaje</Button>
              <Button variant="primary" onClick={() => downloadPdf(buildKartaHtml(selected), `karta-${(selected.jmeno || 'zamestnanec').replace(/\s+/g, '_')}.pdf`)}>📄 Tisk potvrzení</Button>
            </>
          )
        }
      >
        {selected && <KartaDetailBody karta={selected} mode="owner" />}
      </Modal>

      {/* Nová / úprava karty */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editId ? 'Upravit kartu zaměstnance' : 'Nová karta zaměstnance'}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)}>Zrušit</Button>
            <Button variant="primary" onClick={handleSave} disabled={create.isPending || update.isPending || !form.jmeno.trim()}>
              {create.isPending || update.isPending ? 'Ukládám…' : editId ? 'Uložit změny' : 'Založit kartu'}
            </Button>
          </>
        }
      >
        <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
          <DocBlock idx="01" title="Údaje zaměstnance">
            <DocRow k="Jméno a příjmení *" value={form.jmeno} onChange={(v) => setF('jmeno', v)} placeholder="Jan Novák" />
            <DocRow k="Profese / pracovní zařazení" value={form.pozice} onChange={(v) => setF('pozice', v)} placeholder="zedník, stavbyvedoucí…" />
            <DocRow k="Firma" value={form.firma} onChange={(v) => setF('firma', v)} placeholder="název zaměstnavatele" />
            <DocRow
              k="Datum nástupu"
              v={
                <input type="date" value={form.datum_nastupu} onChange={(e) => setF('datum_nastupu', e.target.value)}
                  style={inputStyle} aria-label="Datum nástupu" />
              }
            />
            <DocRow k="Velikost oděvu" value={form.velikost_odevu} onChange={(v) => setF('velikost_odevu', v)} placeholder="např. L / 52" />
            <DocRow k="Velikost obuvi" value={form.velikost_obuvi} onChange={(v) => setF('velikost_obuvi', v)} placeholder="např. 43" />
            <DocRow k="Telefon" value={form.telefon} onChange={(v) => setF('telefon', v)} inputType="tel" placeholder="+420…" />
            <DocRow k="E-mail (pro sdílení)" value={form.email} onChange={(v) => setF('email', v)} inputType="email" placeholder="e-mail zaměstnance" />
            <DocRow k="Jednatel společnosti" value={form.jednatel} onChange={(v) => setF('jednatel', v)} placeholder="jméno jednatele (podpis)" />
            <DocRow k="Osoba odb. způsobilá (BOZP technik)" value={form.bozp_technik} onChange={(v) => setF('bozp_technik', v)} placeholder="jméno / BOZP technik (podpis)" />
            <DocRow k="Poznámka" multiline value={form.poznamka} onChange={(v) => setF('poznamka', v)} />
          </DocBlock>
        </div>
        {(create.isError || update.isError) && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', fontSize: 13 }}>
            Uložení selhalo. Zkuste to prosím znovu.
          </div>
        )}
      </Modal>
    </>
  );
}

// ─── NASDÍLENÉ MNĚ (zaměstnanec) ─────────────────────────────────────────────

function NasdileneKarty() {
  const { data: karty = [], isLoading, error } = useSharedZamestnanci();
  const [detailId, setDetailId] = useState<number | null>(null);
  const selected = detailId != null ? karty.find((k) => k.id === detailId) ?? null : null;

  const columns: ListColumn<ZamestnanecKarta>[] = [
    { key: 'jmeno', label: 'Karta', primary: true, render: (k) => k.jmeno || '—' },
    { key: 'firma', label: 'Firma', width: '180px', dim: true, render: (k) => k.firma || '—' },
    { key: 'owner', label: 'Vystavil', width: '200px', dim: true, render: (k) => k.owner_name || k.owner_email || '—' },
    { key: 'pocet', label: 'Položek', width: '90px', align: 'right', mono: true, render: (k) => String((k.polozky ?? []).length) },
    {
      key: 'obnova',
      label: 'K obnově',
      width: '120px',
      render: (k) => {
        const n = pocetKObnove(k);
        return n > 0 ? <Badge tone="yellow" pill dot>{n}</Badge> : <span style={{ color: 'var(--sd-text-dim)' }}>—</span>;
      },
    },
  ];

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--sd-text-dim)', marginBottom: 12 }}>
        {isLoading ? 'Načítám…' : error ? 'Chyba načítání' : `${karty.length} karet nasdílených vám`}
      </div>
      <ListTable
        columns={columns}
        rows={karty}
        rowKey={(k) => k.id}
        onRowClick={(k) => setDetailId(k.id)}
        emptyTitle="Žádné nasdílené karty"
        emptyText="Karty, které vám zaměstnavatel nasdílí, se objeví zde. Převzetí potvrdíte u jednotlivých položek."
      />

      <Modal
        open={!!selected}
        onClose={() => setDetailId(null)}
        title={selected ? `Karta — ${selected.jmeno || 'zaměstnanec'}` : ''}
        size="xl"
        footer={
          selected && (
            <>
              <Button variant="ghost" onClick={() => setDetailId(null)}>Zavřít</Button>
              <Button variant="secondary" onClick={() => downloadPdf(buildKartaHtml(selected), `karta-${(selected.jmeno || 'zamestnanec').replace(/\s+/g, '_')}.pdf`)}>📄 Tisk potvrzení</Button>
            </>
          )
        }
      >
        {selected && <KartaDetailBody karta={selected} mode="shared" />}
      </Modal>
    </>
  );
}

// ─── DETAIL KARTY (sdílené tělo pro oba pohledy) ──────────────────────────────

const PRAZDNA_POLOZKA = { nazev: '', druh: '', riziko: '', pocet: '1', datum_predani: todayIso(), datum_obnovy: '', datum_vraceni: '', poznamka: '' };

function KartaDetailBody({ karta, mode }: { karta: ZamestnanecKarta; mode: 'owner' | 'shared' }) {
  const createP = useCreateZamestnanecPolozka();
  const updateP = useUpdateZamestnanecPolozka();
  const delP = useDeleteZamestnanecPolozka();
  const accept = useAcceptZamestnanecPolozka();

  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(PRAZDNA_POLOZKA);

  const polozky = karta.polozky ?? [];
  const isOwner = mode === 'owner';

  function setF(k: keyof typeof PRAZDNA_POLOZKA, v: string) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function openNew() {
    setEditId(null);
    setForm(PRAZDNA_POLOZKA);
    setFormOpen(true);
  }

  function openEdit(p: ZamestnanecPolozka) {
    setEditId(p.id);
    setForm({
      nazev: p.nazev,
      druh: p.druh,
      riziko: p.riziko,
      pocet: String(p.pocet ?? 1),
      datum_predani: p.datum_predani || '',
      datum_obnovy: p.datum_obnovy || '',
      datum_vraceni: p.datum_vraceni || '',
      poznamka: p.poznamka,
    });
    setFormOpen(true);
  }

  async function handleSaveItem() {
    const payload = {
      nazev: form.nazev.trim(),
      druh: form.druh.trim(),
      riziko: form.riziko.trim(),
      pocet: parseInt(form.pocet, 10) || 1,
      datum_predani: form.datum_predani || null,
      datum_obnovy: form.datum_obnovy || null,
      datum_vraceni: form.datum_vraceni || null,
      poznamka: form.poznamka.trim(),
    };
    if (editId) await updateP.mutateAsync({ kartaId: karta.id, polozkaId: editId, payload });
    else await createP.mutateAsync({ kartaId: karta.id, payload });
    setFormOpen(false);
  }

  async function handleDeleteItem(p: ZamestnanecPolozka) {
    if (!window.confirm(`Smazat položku „${p.nazev}"?`)) return;
    await delP.mutateAsync({ kartaId: karta.id, polozkaId: p.id });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Hlavička zaměstnance */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 28px', fontSize: 13 }}>
        <Meta label="Profese / zařazení" value={karta.pozice} />
        <Meta label="Firma" value={karta.firma} />
        {karta.datum_nastupu && <Meta label="Datum nástupu" value={fmtDatum(karta.datum_nastupu)} />}
        {(karta.velikost_odevu || karta.velikost_obuvi) && (
          <Meta label="Velikost oděvu / obuvi" value={`${karta.velikost_odevu || '—'} / ${karta.velikost_obuvi || '—'}`} />
        )}
        <Meta label="Telefon" value={karta.telefon} />
        <Meta label="E-mail" value={karta.email} />
        {mode === 'shared' && <Meta label="Vystavil" value={karta.owner_name || karta.owner_email} />}
      </div>
      {karta.poznamka && <div style={{ fontSize: 13, color: 'var(--sd-text-dim)' }}>{karta.poznamka}</div>}

      {/* Položky */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Předané věci a OOPP
          </div>
          {isOwner && <Button size="sm" variant="secondary" onClick={openNew}>+ Přidat položku</Button>}
        </div>

        {polozky.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--sd-text-dim)', padding: '8px 0' }}>Zatím žádné položky.</div>
        ) : (
          <div style={{ border: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: 'var(--sd-w-f7f4ee, #f7f4ee)', textAlign: 'left', color: 'var(--sd-text-dim)' }}>
                  <th style={th}>Věc / OOPP</th>
                  <th style={th}>Druh</th>
                  <th style={{ ...th, textAlign: 'center' }}>Počet</th>
                  <th style={{ ...th, textAlign: 'center' }}>Vydáno</th>
                  <th style={{ ...th, textAlign: 'center' }}>Obnova</th>
                  <th style={{ ...th, textAlign: 'center' }}>Vrácení / vyřazení</th>
                  <th style={{ ...th, textAlign: 'center' }}>Převzetí</th>
                  <th style={{ ...th, width: isOwner ? 96 : 150 }} />
                </tr>
              </thead>
              <tbody>
                {polozky.map((p) => (
                  <tr key={p.id} style={{ borderTop: '1px solid var(--sd-w-efe9dc, #efe9dc)', ...obnovaRowStyle(p.datum_obnovy) }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{p.nazev || '—'}</div>
                      {p.riziko && <div style={{ fontSize: 11.5, color: 'var(--sd-text-dim)' }}>proti: {p.riziko}</div>}
                    </td>
                    <td style={td}>{p.druh || '—'}</td>
                    <td style={{ ...td, textAlign: 'center', fontFamily: 'var(--sd-font-mono)' }}>{p.pocet ?? 1}</td>
                    <td style={{ ...td, textAlign: 'center', fontFamily: 'var(--sd-font-mono)' }}>{fmtDatum(p.datum_predani)}</td>
                    <td style={{ ...td, textAlign: 'center' }}><ObnovaBadge iso={p.datum_obnovy} /></td>
                    <td style={{ ...td, textAlign: 'center', fontFamily: 'var(--sd-font-mono)', color: p.datum_vraceni ? 'var(--sd-text)' : 'var(--sd-text-dim)' }}>
                      {p.datum_vraceni ? fmtDatum(p.datum_vraceni) : '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {p.prijato ? (
                        <Badge tone="success" pill dot>Převzato {fmtDatum(p.prijato_at)}</Badge>
                      ) : (
                        <Badge tone="neutral" pill>Nepotvrzeno</Badge>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isOwner ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>✎</Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(p)} disabled={delP.isPending}>🗑️</Button>
                        </>
                      ) : p.prijato ? (
                        <Button size="sm" variant="ghost" onClick={() => accept.mutate({ polozkaId: p.id, prijato: false })} disabled={accept.isPending}>Zrušit</Button>
                      ) : (
                        <Button size="sm" variant="primary" onClick={() => accept.mutate({ polozkaId: p.id, prijato: true })} disabled={accept.isPending}>✓ Potvrzuji převzetí</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sdílení (jen tvůrce) */}
      {isOwner && <SharePanel kartaId={karta.id} />}

      {/* Přidat / upravit položku (jen tvůrce) */}
      {isOwner && (
        <Modal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          title={editId ? 'Upravit položku' : 'Přidat předanou věc / OOPP'}
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setFormOpen(false)}>Zrušit</Button>
              <Button variant="primary" onClick={handleSaveItem} disabled={createP.isPending || updateP.isPending || !form.nazev.trim()}>
                {createP.isPending || updateP.isPending ? 'Ukládám…' : editId ? 'Uložit změny' : 'Přidat'}
              </Button>
            </>
          }
        >
          <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
            <DocBlock idx="01" title="Předaná věc / OOPP">
              <DocRow k="Název věci *" value={form.nazev} onChange={(v) => setF('nazev', v)} placeholder="Pracovní bunda, ochranná přilba…" />
              <DocRow k="Druh" value={form.druh} onChange={(v) => setF('druh', v)} placeholder="oděv, obuv, přilba, nářadí, telefon…" />
              <DocRow k="Chrání proti riziku" value={form.riziko} onChange={(v) => setF('riziko', v)} placeholder="pád z výšky, pořezání, chlad…" />
              <DocRow
                k="Počet"
                v={
                  <input type="number" min={1} value={form.pocet} onChange={(e) => setF('pocet', e.target.value)}
                    style={inputStyle} aria-label="Počet" />
                }
              />
              <DocRow
                k="Datum vydání"
                v={
                  <input type="date" value={form.datum_predani} onChange={(e) => setF('datum_predani', e.target.value)}
                    style={inputStyle} aria-label="Datum vydání" />
                }
              />
              <DocRow
                k="Plánovaná obnova"
                v={
                  <input type="date" value={form.datum_obnovy} onChange={(e) => setF('datum_obnovy', e.target.value)}
                    style={inputStyle} aria-label="Datum obnovy" />
                }
              />
              <DocRow
                k="Datum vrácení / vyřazení"
                v={
                  <input type="date" value={form.datum_vraceni} onChange={(e) => setF('datum_vraceni', e.target.value)}
                    style={inputStyle} aria-label="Datum vrácení / vyřazení" />
                }
              />
              <DocRow k="Důvod vyřazení / poznámka" multiline value={form.poznamka} onChange={(v) => setF('poznamka', v)} />
            </DocBlock>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <span style={{ color: 'var(--sd-text-dim)' }}>{label}: </span>
      <strong>{value || '—'}</strong>
    </div>
  );
}

const th: CSSProperties = { padding: '7px 9px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td: CSSProperties = { padding: '7px 9px', verticalAlign: 'middle' };
const inputStyle: CSSProperties = { padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600 };

// ─── SDÍLENÍ KARTY ───────────────────────────────────────────────────────────

function SharePanel({ kartaId }: { kartaId: number }) {
  const { data: shares = [] } = useZamestnanecShares(kartaId);
  const add = useAddZamestnanecShare();
  const remove = useRemoveZamestnanecShare();
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);

  async function handleAdd() {
    const e = email.trim();
    if (!e) return;
    setErr(null);
    try {
      await add.mutateAsync({ kartaId, email: e });
      setEmail('');
    } catch (ex: unknown) {
      const status = (ex as { status?: number })?.status;
      setErr(
        status === 404 ? 'Uživatel s tímto e-mailem nenalezen.'
          : status === 409 ? 'Karta je s tímto účtem už sdílena.'
          : status === 400 ? 'Nelze sdílet sám se sebou.'
          : 'Sdílení se nezdařilo.',
      );
    }
  }

  return (
    <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', padding: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        Sdílet se zaměstnancem
      </div>
      <div style={{ fontSize: 12, color: 'var(--sd-text-dim)', marginBottom: 10 }}>
        Zaměstnanec s tímto účtem uvidí svou kartu (jen ke čtení) a bude moci potvrdit převzetí jednotlivých věcí.
      </div>

      {shares.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {shares.map((s) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 10px', background: 'var(--sd-w-f7f4ee, #f7f4ee)', border: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', borderRadius: 4 }}>
              <span style={{ fontSize: 13 }}>
                <strong>{s.shared_with_name || s.shared_with_email}</strong>
                {s.shared_with_name && <span style={{ color: 'var(--sd-text-dim)' }}> · {s.shared_with_email}</span>}
              </span>
              <Button size="sm" variant="ghost" onClick={() => remove.mutate({ kartaId, shareId: s.id })} disabled={remove.isPending}>
                Zrušit
              </Button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e-mail zaměstnance"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          />
        </div>
        <Button variant="secondary" onClick={handleAdd} disabled={add.isPending || !email.trim()}>
          {add.isPending ? 'Sdílím…' : 'Sdílet'}
        </Button>
      </div>
      {err && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--sd-danger)' }}>{err}</div>}
    </div>
  );
}
